"use client";

import Link from "next/link";
import { useQueryState } from "nuqs";
import { Button, buttonVariants } from "@/components/ui/button";
import { DietHubHeroCategoriesCarousel } from "@/features/hubs/components/diet-hub-hero-categories-carousel";
import { DietHubHeroCategory } from "@/features/hubs/components/diet-hub-hero-category";
import type { DietHubHeroMultiType } from "@/features/hubs/types/diet-hub.type";
import { cn } from "@/lib/utils";

export const DietHubHeroMulti = ({
  data,
  title,
  label,
}: {
  title: string;
  label: string;
  data: DietHubHeroMultiType | undefined;
}) => {
  const [selectedCategoryId, _] = useQueryState("category", {
    shallow: true,
    parse: (value) => Number.parseInt(value as string, 10),
    serialize: (value) => value?.toString(),
  });

  const categories = data?.categories;
  const firstCategoryId = categories?.at(0)?.id;
  const resolvedCategoryId =
    selectedCategoryId != null &&
    !Number.isNaN(selectedCategoryId) &&
    categories?.some((c) => c.id === selectedCategoryId)
      ? selectedCategoryId
      : firstCategoryId;

  return (
    <section className="max-lg:pt-12">
      <div className="flex flex-col lg:text-center">
        <p className="font-medium text-brand-violet text-sm uppercase tracking-wide lg:text-base">
          {label}
        </p>
        <h2 className="pt-2 font-medium text-3xl text-brand-green1-300 lg:pt-4 lg:text-5xl">
          {title}
        </h2>
        {data?.callback ? (
          <Button
            className={cn(
              buttonVariants({ variant: "link" }),
              "mx-auto mt-4 hidden max-md:min-h-0 max-md:justify-start max-md:px-0 max-md:py-0 md:ml-auto lg:block",
            )}
            asChild
            variant="link"
          >
            <Link href={data.callback.href}>{data.callback.label}</Link>
          </Button>
        ) : null}
      </div>

      {categories?.length ? (
        <div className="flex flex-col items-center gap-10 pt-4 lg:gap-20 lg:pt-10">
          <DietHubHeroCategoriesCarousel
            activeCategoryId={resolvedCategoryId}
            categories={categories}
          />

          <div className="w-full max-w-full">
            {categories.map((category) => (
              <DietHubHeroCategory
                key={category.id}
                category={category}
                isSelected={category.id === resolvedCategoryId}
              />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};
